// persistPlay.ts - the "real progress" bridge (Phase 2 deploy). A played hand (Diagnose or Solo) carries
// four REAL per-street grades. This module turns them into the D1 rows the progress engine lives on, so
// opening the Progress tab WITHOUT `&seed=1` shows LIVE data derived from what you actually played
// (totals + grade-by-cut), not the seeded sample.
//
// The chain mirrors useImport.save: POST /api/sessions (a decision's hand needs a session FK) → POST
// /api/hands → POST /api/decisions. All three are Access-gated in the Worker; on *.pages.dev the
// personal-mode identity (identity.ts) supplies the owner, and the dev proxy injects a dev identity
// locally, so the web sends no auth header.
//
// SURFACE-NEUTRAL: this module knows nothing about Diagnose's `ResolvedGrade` or Solo's `SoloResult` - each
// surface maps its own per-street grade into `PersistableDecision` (an overall move class + per-dimension
// move classes). That keeps the layering one-way (views depend on this; not the reverse).
//
// WHAT IS REAL vs FIXTURE: the DENORMALIZED columns the dashboard actually reads - street, grade, position,
// villainArchetype, stackDepthBucket, skillScores - are computed from the real played grades. The heavy
// `engineFacts`/`userAction`/`HandRecord` PAYLOADS reuse @postflop/schema's canonical known-good fixtures
// (the sanctioned reuse target for downstream consumers). That is honest for both surfaces, which are
// AUTHORED teaching hands (PRODUCT_NORTH_STAR §5); when generated hands land, swap in the engine's own records.
import {
  validEngineFacts,
  validUserAction,
  validHandRecord,
} from '@postflop/schema/fixtures';
import type { DecisionRecord, HandRecord, SessionRecord, Street } from '@postflop/schema';
import type { MoveClass } from '../grade/types';

/** The four streets in play order; the decision index is the street index. */
const STREETS: readonly Street[] = ['preflop', 'flop', 'turn', 'river'];

/** Monotonic MoveClass → 1..10 (best play → best score). Drives both the decision grade and the
 *  per-dimension skill scores, so a well-played street and a misplayed one land as different bars. */
const SCORE: Record<MoveClass, number> = {
  best: 10,
  great: 9,
  exc: 8,
  inacc: 5,
  mist: 3,
  blun: 1,
  book: 7,
};

/** The teaching hands' fixed context (bridge.ts teaching table: hero in the Cutoff, 150bb, vs a sticker). */
const POSITION = 'CO' as const;
const VILLAIN = 'sticker' as const;
const STACK_BUCKET = '150bb' as const;

/** A surface-neutral graded decision. `gradeClass` is the overall move class → the 1..10 decision grade;
 *  `skillClasses` maps a skill-dimension name to its move class → the 0..10 skill score. */
export interface PersistableDecision {
  gradeClass: MoveClass;
  skillClasses: Record<string, MoveClass>;
}

export interface PersistResult {
  ok: boolean;
  error?: string;
  handId?: string;
  /** number of decision rows written. */
  count?: number;
}

async function postJson<T>(url: string, body: unknown): Promise<{ ok: boolean; status: number; body: T }> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return { ok: res.ok, status: res.status, body: (await res.json()) as T };
}

/**
 * Persist the played streets of one hand. `decisions[i]` is the graded decision for street i, or null if
 * that street was not played (only non-null streets are logged). `mode` labels the session (Diagnose is
 * 'guided', Solo is 'no_tips'). Returns `{ok:false}` with a reason on any failure - the caller treats
 * persistence as best-effort and never blocks the UI on it.
 */
export async function persistPlayedHand(
  decisions: readonly (PersistableDecision | null)[],
  mode: SessionRecord['mode'] = 'guided',
  now: number = Date.now(),
): Promise<PersistResult> {
  const played = decisions
    .map((d, i) => (d ? { d, i } : null))
    .filter((x): x is { d: PersistableDecision; i: number } => x !== null);
  if (played.length === 0) return { ok: false, error: 'no played streets to persist' };

  try {
    // 1. Open a session (Local $1/$3; mode names the surface).
    const session: SessionRecord = { mode, gameType: 'cash', stakesLabel: '$1/$3', startedAt: now };
    const sess = await postJson<{ id?: string; error?: string }>('/api/sessions', session);
    if (!sess.ok || !sess.body.id) return { ok: false, error: sess.body.error ?? `session failed (${sess.status})` };

    // 2. Persist the hand (the FK a decision hangs off; a teaching hand → source 'generated').
    const handRecord: HandRecord = { ...validHandRecord, source: 'generated', seed: `teaching:${mode}:${now}` };
    const hand = await postJson<{ id?: string; error?: string }>('/api/hands', {
      sessionId: sess.body.id,
      handRecord,
    });
    if (!hand.ok || !hand.body.id) return { ok: false, error: hand.body.error ?? `hand failed (${hand.status})` };

    // 3. Persist one graded decision per played street. userId is server-stamped from the identity.
    const decisionRows: DecisionRecord[] = played.map(({ d, i }) => ({
      handId: hand.body.id as string,
      street: STREETS[i],
      decisionIndex: i,
      position: POSITION,
      villainArchetype: VILLAIN,
      stackDepthBucket: STACK_BUCKET,
      engineFacts: validEngineFacts,
      userAction: validUserAction,
      grade: SCORE[d.gradeClass],
      skillScores: Object.fromEntries(
        Object.entries(d.skillClasses).map(([dim, cls]) => [dim, SCORE[cls]]),
      ),
      errorCodes: [],
      firedRuleIds: [],
      createdAt: now + i,
    }));
    const persisted = await postJson<{ count?: number; error?: string }>('/api/decisions', {
      decisions: decisionRows,
    });
    if (!persisted.ok) return { ok: false, error: persisted.body.error ?? `decisions failed (${persisted.status})` };

    return { ok: true, handId: hand.body.id, count: persisted.body.count ?? decisionRows.length };
  } catch {
    return { ok: false, error: 'could not reach the progress service' };
  }
}
